import { useId, useRef, useState } from 'react';

import { AutoAwesome, CircleHelpIcon } from 'components/icons';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Switch } from 'components/ui/switch';
import { Tooltip } from 'components/ui/tooltip';
import { useUiDesign } from 'hooks/useUiDesign';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { FOCUS_RING } from '../focusRing';
import WelcomePopover from '../WelcomePopover';

// The frames draw a 30x18 switch with a 14px knob, between the kit's two sizes: the knob is the switch's only child.
const SWITCH =
    'data-[size=default]:h-[18px] data-[size=default]:w-[30px] data-[size=default]:px-px *:size-3.5! *:shadow-segment *:data-checked:translate-x-3! rtl:*:data-checked:-translate-x-3!';
const TILE = 'bg-primary-wash border-primary-wash-border rounded-md border';
// Inter centres its capitals in the line box, and the lowercase that make up most of the label sit lower. A 16px line
// on 2px of bottom padding keeps the box centred on the icons and lifts the text 1px: its cap height and its x height
// then sit either side of the icons' centre.
const LABEL = 'text-meta pb-0.5 leading-[1.23] font-semibold';
// The ?: a muted glyph in the sparkle's 16px box, drawn 20px, its target grown by `hit-area`.
const WHATS_NEW = cn(
    'text-muted-foreground hover:text-foreground hit-area relative flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm',
    FOCUS_RING
);

type Props = {
    /** On the rail: the sparkle alone, its hover popover holding the switch. */
    collapsed: boolean;
};

/** The new design's switch, always on; turning it off goes back to the classic design. */
function NewDesignRow({ collapsed }: Props) {
    const switchId = useId();
    const anchorRef = useRef<HTMLDivElement>(null);
    const railRef = useRef<HTMLButtonElement>(null);
    const { setDesign } = useUiDesign();
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    const label = t('design.new-design');
    const whatsNewLabel = t('design.whats-new');
    const whatsNew = (
        <Tooltip content={whatsNewLabel}>
            <button
                type="button"
                aria-label={whatsNewLabel}
                className={WHATS_NEW}
                onClick={() => setWhatsNewOpen(true)}
            >
                <CircleHelpIcon className="size-4" />
            </button>
        </Tooltip>
    );

    return (
        <div ref={anchorRef} data-tour="sidebar-new-design">
            {collapsed ? (
                <Popover>
                    <PopoverTrigger
                        ref={railRef}
                        openOnHover
                        aria-label={label}
                        className={cn(
                            TILE,
                            'hit-area relative flex size-[34px] items-center justify-center',
                            FOCUS_RING
                        )}
                    >
                        <AutoAwesome className="text-primary size-4" />
                    </PopoverTrigger>
                    <PopoverContent
                        side="right"
                        sideOffset={10}
                        className="w-auto flex-row items-center gap-3 px-3 py-2"
                    >
                        <label htmlFor={switchId} className={LABEL}>
                            {label}
                        </label>
                        {whatsNew}
                        <Switch
                            id={switchId}
                            checked
                            className={SWITCH}
                            onCheckedChange={() => void setDesign('CLASSIC', 'rail')}
                        />
                    </PopoverContent>
                </Popover>
            ) : (
                // On touch the ?'s 44px area would reach its label across `gap-2.5`, a click there turning the switch.
                <div className={cn(TILE, 'flex h-9 items-center gap-2.5 ps-2.5 pe-2 pointer-coarse:gap-3')}>
                    <AutoAwesome className="text-primary size-4" />
                    <label htmlFor={switchId} className={cn(LABEL, 'sidebar-fade text-foreground cursor-pointer')}>
                        {label}
                    </label>
                    <span className="sidebar-fade me-auto">{whatsNew}</span>
                    <Switch
                        id={switchId}
                        checked
                        className={cn(SWITCH, 'sidebar-fade')}
                        onCheckedChange={() => void setDesign('CLASSIC', 'sidebar')}
                    />
                </div>
            )}
            <WelcomePopover
                anchor={anchorRef}
                reopened={whatsNewOpen}
                onClose={() => setWhatsNewOpen(false)}
                // The rail's popover, and the ? in it, is gone by the time the Welcome closes.
                returnFocus={collapsed ? railRef : undefined}
            />
        </div>
    );
}

export default NewDesignRow;
